// Prerequisites screen (`/prereqs`) — README §6.2.
//
// Title header (recipe meta), launch-pad boxes (per-kind prereqs +
// Planner on row one, Hotspots + Notes on row two), and a full-width
// Start Cooking button that is greyed until every
// ingredient/utensil/equipment item has been ticked.
//
// Planner / Hotspots / Notes are purely informational: opening them
// shifts their launch box to a quiet "read" tone, but they never gate
// progression. The /anchor call that the cook needs to be anchored to
// wall-clock happens at Start Cooking press time, using "now + total
// cook duration" as the serve time. When the call succeeds we navigate
// to /cook.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Chip, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { anchor as apiAnchor } from '../api/client';
import { ApiError } from '../api/errors';
import { durationToSeconds, formatDurationHM } from '../cooking/helpers';
import { ensureAudioReady } from '../cooking/sounds';
import { derivedPrepLeadSeconds, shouldDerivePrepChecklist } from '../cooking/derivePrep';
import { useAppStore, selectStartCookingReady } from '../state/store';

const COURSE_LABEL: Record<string, string> = {
  starter: 'Starter',
  main: 'Main',
  dessert: 'Dessert',
};

function countPhases(meta: { preCook: boolean; prepCook: boolean; liveCook: boolean }): number {
  return (meta.preCook ? 1 : 0) + (meta.prepCook ? 1 : 0) + (meta.liveCook ? 1 : 0);
}

// Small monochrome clock face for the phase-count chip — inherits the
// chip's text colour and scales with its font size. Decorative; the chip
// carries the meaning via its aria-label.
function ClockGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="0.9em"
      height="0.9em"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 7v5l3.5 2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatServe(instant: string): string {
  const d = new Date(instant);
  if (!Number.isFinite(d.getTime())) return instant;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function clientZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

interface LaunchBoxProps {
  title: string;
  detail: string;
  onClick: () => void;
  ariaLabel?: string;
  tone?: 'quiet' | 'read';
}

function LaunchBox({ title, detail, onClick, ariaLabel, tone = 'quiet' }: LaunchBoxProps) {
  const background = tone === 'read' ? theme.bg.muted : theme.bg.raised;
  const colour = tone === 'read' ? theme.text.primary : theme.text.primary;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? title}
      data-tone={tone}
      style={{
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
        background,
        border: tone === 'read' ? `1px solid ${theme.accent.calm}` : 'none',
        borderRadius: '10px',
        padding: '1rem',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        color: colour,
        minHeight: '88px',
      }}
    >
      <div style={{ fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: '0.9rem', color: theme.text.muted }}>{detail}</div>
    </button>
  );
}

export default function Prereqs() {
  const navigate = useNavigate();
  const meta = useAppStore((s) => s.meta);
  const recipe = useAppStore((s) => s.recipe);
  const recipeId = useAppStore((s) => s.recipeId);
  const checked = useAppStore((s) => s.checked);
  const visited = useAppStore((s) => s.visited);
  const anchoredPlan = useAppStore((s) => s.anchoredPlan);
  const serveTime = useAppStore((s) => s.serveTime);
  const setAnchoredPlan = useAppStore((s) => s.setAnchoredPlan);
  const setServeTime = useAppStore((s) => s.setServeTime);
  // Subscribe to the derived boolean so the button shares the same store
  // snapshot as the counters (no imperative get() during render — see
  // selectStartCookingReady). Re-renders only when readiness actually flips.
  const ready = useAppStore(selectStartCookingReady);

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  // If the user landed here without validating a file, send them home.
  if (!meta || !recipe) {
    return (
      <main style={{ padding: '1rem' }}>
        <Wordmark size="md" as="h1" />
        <p style={{ color: theme.text.muted }}>Pick a recipe first.</p>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </main>
    );
  }

  const ingredientsCount = recipe.cookpit.ingredients?.length ?? 0;
  const utensilsCount = recipe.cookpit.utensils?.length ?? 0;
  const equipmentCount = recipe.cookpit.equipment?.length ?? 0;
  const ingChecked = Object.values(checked.ingredients).filter(Boolean).length;
  const utChecked = Object.values(checked.utensils).filter(Boolean).length;
  const eqChecked = Object.values(checked.equipment).filter(Boolean).length;

  const hotspotsCount =
    (recipe.cookpit.prerequisites?.hotspots?.length ?? 0) +
    (anchoredPlan?.hotspots.filter((h) => h.source === 'computed').length ?? 0);
  const notesCount =
    (recipe.cookpit.prerequisites?.skills?.length ?? 0) +
    (recipe.cookpit.prerequisites?.notes?.length ?? 0);

  const serveLabel = serveTime ? formatServe(serveTime.instant) : 'Tap to set serve time';

  const courseChip = COURSE_LABEL[meta.selectedCourse] ?? meta.selectedCourse;

  const derivesPrep = shouldDerivePrepChecklist(recipe);

  // Total cook duration in seconds — used to pick a default serve time
  // when the user pressed Start Cooking without visiting the Planner.
  function totalCookSeconds(): number {
    const live = durationToSeconds(recipe!.cookpit.liveCook.nominalDuration);
    const pre = recipe!.cookpit.preCook
      ? durationToSeconds(recipe!.cookpit.preCook.nominalDuration)
      : 0;
    const prep = recipe!.cookpit.prepCook
      ? durationToSeconds(recipe!.cookpit.prepCook.nominalDuration)
      : 0;
    // A derived PrepCook checklist occupies wall-clock before LiveCook, so
    // the default serve time must allow for it: its declared prepTime, else
    // the 30-minute advisory default.
    const derivedPrep = derivedPrepLeadSeconds(recipe);
    return live + pre + prep + derivedPrep;
  }

  async function onStartCooking(): Promise<void> {
    if (!recipe || !recipeId || !meta) return;
    // Prime audio inside this real user gesture so the first activation cue at
    // the very start of the cook isn't swallowed by the browser autoplay policy
    // (sound is on by default, so the cook never taps the speaker toggle).
    ensureAudioReady();
    setStartError(null);

    // Fast path — the user already anchored via Planner in a previous
    // session-state, or the schedule is still warm. Nothing to fetch.
    if (anchoredPlan) {
      navigate('/cook');
      return;
    }

    setStarting(true);
    try {
      // Serve time defaults to "now + total cook duration" — the cook
      // is starting now, by definition. If the user picked an explicit
      // serve time on Planner, honour it.
      const serveInstant =
        serveTime?.instant ?? new Date(Date.now() + totalCookSeconds() * 1000).toISOString();
      const zone = serveTime?.zone ?? clientZone();
      const plan = await apiAnchor({
        recipeId,
        recipe,
        selectedCourse: meta.selectedCourse,
        serveTime: { instant: serveInstant, zone },
        prereqsConfirmed: true,
      });
      setAnchoredPlan(plan, plan.planToken);
      setServeTime(plan.serveTime);
      navigate('/cook');
    } catch (err) {
      if (err instanceof ApiError) {
        setStartError(err.message);
      } else {
        setStartError("Couldn't reach the cooking service — try again in a moment.");
      }
    } finally {
      setStarting(false);
    }
  }

  return (
    <main
      style={{
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        maxWidth: '40rem',
        margin: '0 auto',
        minHeight: '100vh',
      }}
    >
      <header
        style={{
          background: theme.bg.raised,
          padding: '1rem',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <Wordmark size="sm" as="span" />
        <h2 style={{ margin: 0, fontSize: '1.4rem' }}>{meta.name}</h2>
        {meta.author && <div style={{ color: theme.text.muted }}>by {meta.author}</div>}
        {meta.source && (
          <div style={{ color: theme.text.muted, fontSize: '0.9rem' }}>{meta.source}</div>
        )}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignItems: 'center',
            marginTop: '0.25rem',
          }}
        >
          <Chip text={courseChip} variant="course" />
          {meta.cuisine && <Chip text={meta.cuisine} variant="lane" />}
          <Chip text={meta.difficulty} variant="lane" />
          <Chip
            text={`${countPhases(meta.phases) + (derivesPrep ? 1 : 0)}`}
            ariaLabel={`${countPhases(meta.phases) + (derivesPrep ? 1 : 0)} of 3 phases`}
            icon={<ClockGlyph />}
            variant="lane"
          />
          {meta.prepTime && (
            <Chip
              text={`prep ${formatDurationHM(durationToSeconds(meta.prepTime))}`}
              variant="lane"
            />
          )}
          {meta.totalTime && (
            <Chip
              text={`total ${formatDurationHM(durationToSeconds(meta.totalTime))}`}
              variant="lane"
            />
          )}
          <Chip text="Verified ✓" variant="verified" />
        </div>
      </header>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'stretch' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {ingredientsCount > 0 && (
            <LaunchBox
              title="Ingredients"
              ariaLabel={`Ingredients, ${ingChecked} of ${ingredientsCount} ticked`}
              detail={`${ingChecked} / ${ingredientsCount}`}
              onClick={() => navigate('/ingredients')}
            />
          )}
          {utensilsCount > 0 && (
            <LaunchBox
              title="Utensils"
              ariaLabel={`Utensils, ${utChecked} of ${utensilsCount} ticked`}
              detail={`${utChecked} / ${utensilsCount}`}
              onClick={() => navigate('/utensils')}
            />
          )}
          {equipmentCount > 0 && (
            <LaunchBox
              title="Equipment"
              ariaLabel={`Equipment, ${eqChecked} of ${equipmentCount} ticked`}
              detail={`${eqChecked} / ${equipmentCount}`}
              onClick={() => navigate('/equipment')}
            />
          )}
        </div>
        <div style={{ flex: 1, display: 'flex' }}>
          <LaunchBox
            title="Planner"
            detail={serveLabel}
            tone={visited.planner ? 'read' : 'quiet'}
            ariaLabel={
              visited.planner ? `Planner — opened. ${serveLabel}` : `Planner. ${serveLabel}`
            }
            onClick={() => navigate('/planner')}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <LaunchBox
          title="Hotspots"
          detail={`${hotspotsCount} ${hotspotsCount === 1 ? 'squeeze point' : 'squeeze points'}`}
          tone={visited.hotspots ? 'read' : 'quiet'}
          ariaLabel={visited.hotspots ? 'Hotspots — opened' : 'Hotspots'}
          onClick={() => navigate('/hotspots')}
        />
        <LaunchBox
          title="Notes"
          detail={`${notesCount} ${notesCount === 1 ? 'technique' : 'techniques'}`}
          tone={visited.notes ? 'read' : 'quiet'}
          ariaLabel={visited.notes ? 'Notes — opened' : 'Notes'}
          onClick={() => navigate('/notes')}
        />
      </div>

      <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <Button
          variant="primary"
          state={ready && !starting ? 'enabled' : 'disabled'}
          onClick={() => {
            void onStartCooking();
          }}
        >
          {starting ? 'Starting…' : 'Start Cooking'}
        </Button>
        {!ready && (
          <p style={{ margin: 0, fontSize: '0.85rem', color: theme.text.muted }}>
            Tick everything in Ingredients, Utensils and Equipment to start cooking.
          </p>
        )}
        {startError && (
          <div
            role="alert"
            style={{
              padding: '0.75rem',
              background: theme.bg.muted,
              borderRadius: '8px',
              color: theme.text.primary,
              fontSize: '0.9rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              alignItems: 'flex-start',
            }}
          >
            <span>{startError}</span>
            <Button
              variant="secondary"
              state={starting ? 'disabled' : 'enabled'}
              onClick={() => {
                void onStartCooking();
              }}
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
